import {
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { FiAlertTriangle, FiChevronDown, FiClock } from 'react-icons/fi';

interface UpdateDurationModalV2Props {
  isOpen: boolean;
  onClose: () => void;
  calculatedDuration: number;
  editedDuration: number | null;
  onConfirm: (minutes: number, reason: string) => void;
}

export const REASONS = {
  TECHNICAL_DIFFICULTIES: 'Technical Difficulties',
  POOR_INTERNET: 'Poor Internet Connection',
  PATIENT_PHONE_CALL: 'Patient Phone Call Request',
  OTHER: 'Other',
} as const;

const REASON_OPTIONS = [
  {
    label: REASONS.TECHNICAL_DIFFICULTIES,
    value: REASONS.TECHNICAL_DIFFICULTIES,
  },
  {
    label: REASONS.POOR_INTERNET,
    value: REASONS.POOR_INTERNET,
  },
  {
    label: REASONS.PATIENT_PHONE_CALL,
    value: REASONS.PATIENT_PHONE_CALL,
  },
  {
    label: REASONS.OTHER,
    value: REASONS.OTHER,
  },
];

/**
 * V2 of UpdateDurationModal with callback pattern instead of setState.
 *
 * Changes from V1:
 * - Uses onConfirm callback instead of setEditedDuration
 * - Internal state for form fields (reason, otherReason, wantsToUpdate)
 * - Calls onConfirm(minutes, reason) when confirmed
 *
 * Features preserved:
 * - Compliance warning about billing falsification
 * - Acknowledgment checkbox required
 * - Reason selection with "Other" text field
 * - Validation (must check box, select reason, enter duration)
 */
const UpdateDurationModalV2: React.FC<UpdateDurationModalV2Props> = ({
  isOpen,
  onClose,
  calculatedDuration,
  editedDuration,
  onConfirm,
}) => {
  const [wantsToUpdate, setWantsToUpdate] = useState(false);
  const [actualDuration, setActualDuration] = useState<string | number>(
    calculatedDuration,
  );
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      setActualDuration(editedDuration ?? calculatedDuration);
      setWantsToUpdate(editedDuration !== null);
      setSelectedReason('');
      setOtherReason('');
    }
  }, [isOpen, calculatedDuration, editedDuration]);

  // Auto-focus textarea when "Other" is selected
  useEffect(() => {
    if (selectedReason === REASONS.OTHER && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [selectedReason]);

  // Allow empty string for better UX
  const handleInputChange = (value: string) => {
    setActualDuration(value === '' ? '' : Number(value));
  };

  const handleCancel = () => {
    setWantsToUpdate(false);
    setSelectedReason('');
    setOtherReason('');
    onClose();
  };

  // Confirm and call parent's onConfirm with duration and reason
  const handleConfirmDurationUpdate = () => {
    const durationValue = actualDuration === '' ? 0 : Number(actualDuration);
    const finalReason =
      selectedReason === REASONS.OTHER ? otherReason : selectedReason;

    // Call parent's callback
    onConfirm(durationValue, finalReason);

    // Reset form
    setWantsToUpdate(false);
    setSelectedReason('');
    setOtherReason('');
  };

  // Determine if confirm button should be disabled
  const isReasonValid =
    selectedReason &&
    (selectedReason !== REASONS.OTHER || otherReason.trim() !== '');
  const isButtonDisabled =
    !wantsToUpdate || actualDuration === '' || !isReasonValid;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} isCentered>
      <ModalOverlay />
      <ModalContent
        bg="surface.default"
        overflow="hidden"
        borderColor="border.default"
        borderWidth={1}
        borderRadius={10}
        color="text.default"
        maxWidth="560px"
      >
        <ModalHeader as={HStack} mt={5} pb={0} gap={2}>
          <FiAlertTriangle size={20} color="orange" />
          <Heading size="md" fontFamily={'Plus Jakarta Sans'}>
            Warning: Manual Duration Update
          </Heading>
        </ModalHeader>
        <ModalBody
          as={VStack}
          gap={6}
          position="relative"
          mb={3}
          textAlign="left"
          marginTop={3}
          alignItems="flex-start"
        >
          <Text color="text.light" lineHeight="1.6" fontSize="14px">
            Updating an appointment to show more time than was spent in direct
            patient interaction is not compliant and may be considered billing
            falsification. If this is due to a technical issue, check the box
            below to confirm. All updates are subject to audit and misuse may
            lead to immediate deplatforming. If you have any questions, reach
            out to{' '}
            <Text
              as="a"
              href="mailto:help@berrystreet.co"
              color="blue.400"
              textDecoration="underline"
            >
              help@berrystreet.co
            </Text>
          </Text>

          <Checkbox
            isChecked={wantsToUpdate}
            onChange={(e) => setWantsToUpdate(e.target.checked)}
            colorScheme="green"
            data-testid="acknowledge-checkbox"
          >
            <Text fontSize="14px" color="text.default">
              I acknowledge that I have read and agree to the above information.
            </Text>
          </Checkbox>

          <VStack w="full" gap="1rem">
            <HStack
              as={FormControl}
              w="full"
              justifyContent="space-between"
              alignItems="center"
            >
              <HStack
                as={FormLabel}
                size="sm"
                mb={0}
                opacity={wantsToUpdate ? 1 : 0.5}
              >
                <FiClock />
                <span>Actual duration:</span>
              </HStack>
              <Input
                isDisabled={!wantsToUpdate}
                isRequired
                w="120px"
                value={actualDuration}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={(e) => e.target.select()}
                type="number"
                bg="surface.light"
                color="text.default"
                data-testid="duration-input"
                _disabled={{
                  color: 'text.muted',
                  opacity: 0.6,
                }}
              />
            </HStack>

            <HStack
              as={FormControl}
              w="full"
              justifyContent="space-between"
              alignItems="center"
            >
              <HStack
                as={FormLabel}
                size="sm"
                mb={0}
                minW="140px"
                opacity={wantsToUpdate ? 1 : 0.5}
              >
                <span>Reason:</span>
              </HStack>
              <Menu matchWidth placement="bottom">
                {({ isOpen }) => (
                  <>
                    <MenuButton
                      as={Button}
                      width="260px"
                      justifyContent="space-between"
                      bg="surface.light"
                      rightIcon={
                        <FiChevronDown
                          style={{
                            transform: isOpen
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                            transition: 'transform 0.1s ease-in-out',
                          }}
                        />
                      }
                      fontWeight="normal"
                      textAlign="left"
                      border="1px solid"
                      borderColor="border.default"
                      isDisabled={!wantsToUpdate}
                      data-testid="reason-select"
                      color="text.default"
                      _hover={{
                        bg: 'surface.light',
                        borderColor: 'border.default',
                      }}
                      _active={{
                        bg: 'surface.light',
                      }}
                      _disabled={{
                        color: 'text.muted',
                        opacity: 0.6,
                      }}
                    >
                      {selectedReason || 'Select reason for update'}
                    </MenuButton>
                    <MenuList bg="surface.light">
                      {REASON_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.value}
                          onClick={() => setSelectedReason(option.value)}
                          bg="surface.light"
                          color="text.default"
                          opacity={0.8}
                          _hover={{
                            bg: 'surface.dark',
                            color: 'text.default',
                            opacity: 1,
                          }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </>
                )}
              </Menu>
            </HStack>

            <Collapse
              in={selectedReason === REASONS.OTHER}
              style={{ width: '100%' }}
            >
              <VStack w="full" alignItems="flex-start">
                <Text fontSize="14px" color="text.muted" mb={2}>
                  Please provide additional details below:
                </Text>
                <Textarea
                  ref={textareaRef}
                  isDisabled={!wantsToUpdate}
                  isRequired
                  w="full"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  placeholder="Enter reason for duration update..."
                  bg="surface.light"
                  resize="vertical"
                  data-testid="other-reason-textarea"
                  color="text.default"
                  _placeholder={{
                    color: 'text.muted',
                  }}
                  minH="80px"
                  _disabled={{
                    color: 'text.muted',
                    opacity: 0.6,
                  }}
                />
              </VStack>
            </Collapse>
          </VStack>
        </ModalBody>
        <ModalFooter justifyContent="center" mb={6} gap={10}>
          <Button
            variant="outline"
            color="text.default"
            borderColor="border.default"
            onClick={handleCancel}
            data-testid="cancel-update-button"
            _hover={{
              color: 'green.600',
              bgColor: 'surface.light',
              borderColor: 'green.600',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="white"
            bg="orange.500"
            isDisabled={isButtonDisabled}
            onClick={handleConfirmDurationUpdate}
            data-testid="confirm-update-button"
            _hover={{
              bg: 'orange.600',
            }}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UpdateDurationModalV2;
